/**
 * Generate abbreviation from hospital name
 * Takes first letter of each word (max 2 words, 2 chars each = 4 chars max)
 * Examples: "AMCare Medical" -> "AM", "City Hospital" -> "CH"
 */
export function getPatientIdPrefix(hospitalName?: string): string {
  const hospitalAbbr = hospitalName ? getHospitalAbbreviation(hospitalName) : 'HO';
  const year = new Date().getFullYear().toString().slice(-2);
  return `${hospitalAbbr}${year}`;
}

function getHospitalAbbreviation(hospitalName: string): string {
  if (!hospitalName) return 'HO'; // Default if no name
  
  // Remove common words like "Hospital", "Clinic", "Medical", "Center"
  const commonWords = ['hospital', 'clinic', 'medical', 'center', 'centre', 'healthcare', 'health'];
  const words = hospitalName
    .toUpperCase()
    .split(/\s+/)
    .filter(word => !commonWords.includes(word.toLowerCase()))
    .slice(0, 2); // Take first 2 words
  
  if (words.length === 0) {
    // If all words were filtered out, use first 2 letters of name
    return hospitalName.substring(0, 2).toUpperCase().padEnd(2, 'H');
  }
  
  // Take first letter of each word
  const abbreviation = words.map(word => word.charAt(0)).join('');
  return abbreviation.padEnd(2, 'H'); // Ensure 2 characters
}

/**
 * Generate patient ID in format: HHYY-XXXX
 * HH = Hospital abbreviation (2 letters)
 * YY = Year (last 2 digits)
 * XXXX = Sequential number (0000-9999)
 */
export async function generatePatientId(patients: any[], hospitalName?: string): Promise<string> {
  // Get hospital abbreviation
  let hospitalAbbr = 'HO'; // Default
  
  if (hospitalName) {
    hospitalAbbr = getHospitalAbbreviation(hospitalName);
  } else {
    // Try to get from context
    try {
      const { useHospital } = await import('../hooks/useHospital');
      const hospital = useHospital.getState().currentHospital;
      if (hospital?.name) {
        hospitalAbbr = getHospitalAbbreviation(hospital.name);
      }
    } catch (e) {
      console.warn('Could not get hospital name for ID generation:', e);
    }
  }
  
  // Get current year (last 2 digits)
  const year = new Date().getFullYear().toString().slice(-2);
  const prefix = `${hospitalAbbr}${year}`;
  
  // Get all patients registered this year with this hospital prefix
  const yearPatients = patients.filter(p => {
    // Support both old format (YY-XXXX) and new format (HHYY-XXXX)
    return p.id.startsWith(prefix) || p.id.startsWith(year);
  });
  
  // Get the highest sequence number for this year
  const maxSeq = yearPatients.reduce((max, p) => {
    // Extract sequence from ID (format: HHYY-XXXX or YY-XXXX)
    const parts = p.id.split('-');
    if (parts.length === 2) {
      const seq = parseInt(parts[1] || '0');
      return Math.max(max, seq);
    }
    return max;
  }, 0);
  
  // Generate next sequence number (4 digits, 0-9999)
  const nextSeq = Math.min((maxSeq + 1), 9999).toString().padStart(4, '0');
  
  // Combine to create ID in format: HHYY-XXXX
  return `${prefix}-${nextSeq}`;
}

/**
 * Generate patient ID synchronously (for backward compatibility)
 * Uses 'HO' as default hospital abbreviation if hospital name not provided
 */
function maxPatientSeqFromIds(ids: string[], prefix: string): number {
  return ids.reduce((max, id) => {
    if (!id?.startsWith(`${prefix}-`)) return max;
    const parts = id.split('-');
    if (parts.length === 2) {
      const seq = parseInt(parts[1] || '0', 10);
      return Math.max(max, Number.isNaN(seq) ? 0 : seq);
    }
    return max;
  }, 0);
}

export function generatePatientIdSync(
  patients: any[],
  hospitalName?: string,
  extraIds: string[] = []
): string {
  const hospitalAbbr = hospitalName ? getHospitalAbbreviation(hospitalName) : 'HO';
  const year = new Date().getFullYear().toString().slice(-2);
  const prefix = `${hospitalAbbr}${year}`;

  const localIds = patients
    .filter((p) => p.id?.startsWith(prefix) || p.id?.startsWith(year))
    .map((p) => p.id as string);
  const maxSeq = maxPatientSeqFromIds([...localIds, ...extraIds], prefix);
  const nextSeq = Math.min(maxSeq + 1, 9999).toString().padStart(4, '0');

  return `${prefix}-${nextSeq}`;
}

/** Increment HHYY-XXXX sequence (patient ids are globally unique). */
export function bumpPatientId(id: string): string {
  const parts = id.split('-');
  if (parts.length !== 2) return id;
  const prefix = parts[0];
  const seq = Math.min((parseInt(parts[1], 10) || 0) + 1, 9999);
  return `${prefix}-${seq.toString().padStart(4, '0')}`;
}

export function formatPatientId(id: string): string {
  // If ID is a UUID (contains 4 dashes), return a shortened version
  if (id.includes('-') && id.split('-').length === 5) {
    return `ID-${id.substring(0, 8).toUpperCase()}`;
  }
  
  // If ID is already in correct format (HHYY-XXXX or YY-XXXX), return as is
  if (id.includes('-') && id.split('-').length === 2) {
    return id;
  }
  
  // If ID is just numbers, format it as YY-XXXX (backward compatibility)
  const year = new Date().getFullYear().toString().slice(-2);
  return `${year}-${id.padStart(4, '0')}`;
}

/**
 * Generate prescription ID in format: PRES-YY-XXXX
 * PRES = Prefix for prescription
 * YY = Year (last 2 digits)
 * XXXX = Sequential number (0000-9999)
 */
export function generatePrescriptionId(prescriptions: any[]): string {
  // Get current year (last 2 digits)
  const year = new Date().getFullYear().toString().slice(-2);
  const prefix = `PRES-${year}`;
  
  // Get all prescriptions created this year
  const yearPrescriptions = prescriptions.filter(p => {
    if (!p.id) return false;
    // Support both UUID and formatted ID
    return p.id.startsWith(prefix) || p.id.includes(year);
  });
  
  // Get the highest sequence number for this year
  const maxSeq = yearPrescriptions.reduce((max, p) => {
    if (!p.id) return max;
    
    // Extract sequence from ID (format: PRES-YY-XXXX)
    if (p.id.startsWith(prefix)) {
      const parts = p.id.split('-');
      if (parts.length === 3) {
        const seq = parseInt(parts[2] || '0');
        return Math.max(max, seq);
      }
    }
    return max;
  }, 0);
  
  // Generate next sequence number (4 digits, 0-9999)
  const nextSeq = Math.min((maxSeq + 1), 9999).toString().padStart(4, '0');
  
  // Combine to create ID in format: PRES-YY-XXXX
  return `${prefix}-${nextSeq}`;
}

/**
 * Format prescription ID for display
 */
export function formatPrescriptionId(id: string): string {
  // If ID is a UUID (contains 4 dashes), return a shortened version
  if (id.includes('-') && id.split('-').length === 5) {
    return `PRES-${id.substring(0, 8).toUpperCase()}`;
  }
  
  // If ID is already in correct format (PRES-YY-XXXX), return as is
  if (id.startsWith('PRES-')) {
    return id;
  }
  
  // If ID is just numbers or UUID, return as is (for backward compatibility)
  return id;
}

/**
 * Generate master patient ID in format: MP-YYYY-XXXX
 * MP = Master Patient prefix
 * YYYY = Year (4 digits)
 * XXXX = Sequential number (0001-9999)
 */
function maxMasterPatientSeqFromIds(ids: string[], year: string): number {
  const prefix = `MP-${year}-`;
  return ids.reduce((max, id) => {
    if (!id?.startsWith(prefix)) return max;
    const parts = id.split('-');
    if (parts.length === 3) {
      const seq = parseInt(parts[2] || '0', 10);
      return Math.max(max, Number.isNaN(seq) ? 0 : seq);
    }
    return max;
  }, 0);
}

export function generateMasterPatientId(masterPatients: any[], extraIds: string[] = []): string {
  const year = new Date().getFullYear().toString();
  const prefix = `MP-${year}`;

  const localIds = masterPatients.map((p) => p.id).filter(Boolean) as string[];
  const maxSeq = maxMasterPatientSeqFromIds([...localIds, ...extraIds], year);
  const nextSeq = Math.min(maxSeq + 1, 9999).toString().padStart(4, '0');

  return `${prefix}-${nextSeq}`;
}

/** Increment MP-YYYY-XXXX sequence (IDs are globally unique across hospitals). */
export function bumpMasterPatientId(id: string): string {
  const year = new Date().getFullYear().toString();
  const prefix = `MP-${year}-`;
  if (!id.startsWith(prefix)) return id;
  const parts = id.split('-');
  if (parts.length !== 3) return id;
  const seq = Math.min((parseInt(parts[2], 10) || 0) + 1, 9999);
  return `${prefix}${seq.toString().padStart(4, '0')}`;
}

export function generateBillNumber(patientId: string, patientBills: any[]): string {
  // Extract parts from patient ID (format: HHYY-XXXX or YY-XXXX)
  const parts = patientId.split('-');
  
  // Handle both old format (YY-XXXX) and new format (HHYY-XXXX)
  let year, seq;
  if (parts.length === 2) {
    // New format: HHYY-XXXX or old format: YY-XXXX
    const prefix = parts[0];
    // If prefix is 4 chars, it's HHYY, extract last 2 for year
    if (prefix.length === 4) {
      year = prefix.substring(2);
      seq = parts[1];
    } else {
      // Old format: YY-XXXX
      year = prefix;
      seq = parts[1];
    }
  } else {
    // Fallback
    year = new Date().getFullYear().toString().slice(-2);
    seq = '0001';
  }
  
  // Count existing bills for this patient
  // patientBills should be an array of Bill objects with patientId property
  const billCount = patientBills.filter(bill => {
    // Handle both Bill objects (with patientId) and raw bill data (with patient_id)
    const billPatientId = bill.patientId || bill.patient_id;
    return billPatientId === patientId;
  }).length;
  
  // Generate 2-digit bill sequence number (01, 02, 03, etc.)
  const billSeq = (billCount + 1).toString().padStart(2, '0');
  
  // Combine to create bill number in format: YY-XXXX-WW (keeping old format for bills)
  return `${year}-${seq}-${billSeq}`;
} 